import { cn } from '../ui/utils';
import { LucideIcon } from 'lucide-react';
import { memo } from 'react';

interface CategoryBadgeProps {
  /**
   * Category label
   */
  label: string;
  /**
   * Category icon
   */
  icon?: LucideIcon;
  /**
   * Category type (affects color)
   */
  type?: 'income' | 'expense' | 'transfer' | 'budget' | 'neutral';
  /**
   * Variant style
   * @default 'default'
   */
  variant?: 'default' | 'outline' | 'ghost';
  /**
   * Size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Custom color override
   */
  color?: string;
  className?: string;
  /**
   * Test ID for testing
   * @default 'category-badge'
   */
  testId?: string;
}

const sizeClasses = {
  sm: 'text-xs px-2 py-0.5 gap-1',
  md: 'text-sm px-2.5 py-1 gap-1.5',
  lg: 'text-base px-3 py-1.5 gap-2',
};

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

const typeColors = {
  income: {
    default: 'bg-income-bg text-income border-success-200 dark:border-success-900',
    outline: 'border-income text-income bg-transparent',
    ghost: 'text-income hover:bg-income-bg',
  },
  expense: {
    default: 'bg-expense-bg text-expense border-danger-200 dark:border-danger-900',
    outline: 'border-expense text-expense bg-transparent',
    ghost: 'text-expense hover:bg-expense-bg',
  },
  transfer: {
    default: 'bg-transfer-bg text-transfer border-info-200 dark:border-info-900',
    outline: 'border-transfer text-transfer bg-transparent',
    ghost: 'text-transfer hover:bg-transfer-bg',
  },
  budget: {
    default: 'bg-budget-bg text-budget border-warning-200 dark:border-warning-900',
    outline: 'border-budget text-budget bg-transparent',
    ghost: 'text-budget hover:bg-budget-bg',
  },
  neutral: {
    default: 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:border-neutral-700',
    outline: 'border-neutral-400 text-neutral-700 bg-transparent dark:text-neutral-300',
    ghost: 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800',
  },
};

/**
 * CategoryBadge - Displays financial category with icon and color
 * 
 * Used to show transaction categories with semantic colors.
 * Automatically applies appropriate color based on type.
 * 
 * @example
 * ```tsx
 * <CategoryBadge
 *   label="Vendas"
 *   type="income"
 *   icon={TrendingUp}
 * />
 * 
 * <CategoryBadge
 *   label="Marketing"
 *   type="expense"
 *   variant="outline"
 * />
 * ```
 */
export const CategoryBadge = memo(function CategoryBadge({
  label,
  icon: Icon,
  type = 'neutral',
  variant = 'default',
  size = 'md',
  color,
  className,
  testId = 'category-badge',
}: CategoryBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium transition-colors',
        sizeClasses[size],
        typeColors[type][variant],
        className
      )}
      style={color ? { backgroundColor: color + '20', color, borderColor: color } : undefined}
      data-testid={testId}
      data-type={type}
    >
      {Icon && (
        <Icon className={iconSizes[size]} aria-hidden="true" />
      )}
      <span data-testid={`${testId}-label`}>{label}</span>
    </span>
  );
});
